import { useContext, useEffect, useState } from 'react';

/** @format */
import { Formik } from 'formik';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import * as Yup from 'yup';

import { EventContext } from '../../Contexts/EventContext';
import DatePicker from '../../Shared/Components/Datepicker';
import ErrorMessage from '../../Shared/Components/ErrorMessage';
import FileUploader from '../../Shared/Components/FileUploader';
import Loading from '../../Shared/Components/Loading';
import SunTextEditor from '../../Shared/Components/SunTextEditor';
import { error, success } from '../../Shared/Components/Toast';
import useApi from '../../Shared/hooks/api/Index';
import { useRole } from '../../Shared/hooks/CustomHooks';
import { getStoredAuthToken } from '../../Shared/utils/authToken';
import {
  baseApiUrl,
  getRegistredBy,
  jalaliDateToGregorianApi,
} from '../../Shared/utils/helper';
import Master from '../Layouts/Master';

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required('عنوان الزامی است')
    .max(190, 'عنوان نمیتواند بیشتر از 190 کاراکتر باشد'),
  organizer_id: Yup.string().required('انتخاب برگزار کننده الزامی است'),
  category_id: Yup.string().required('انتخاب دسته بندی الزامی است'),
  form_id: Yup.string()
    .required('انتخاب فرم الزامی است')
    .min(1, 'انتخاب فرم الزامی است'),
  registered_by: Yup.string().required('انتخاب ثبت نام کننده الزامی است'),
  start_register_date: Yup.string().required('زمان شروع ثبت نام الزامی است'),
  end_register_date: Yup.string().required('زمان پایان ثبت نام الزامی است'),
  time: Yup.string().required('زمان الزامی است'),
  place: Yup.string().nullable(true),
  gender: Yup.string().required('جنسیت الزامی است'),
  province_id: Yup.string().required('انتخاب استان الزامی است'),
  type: Yup.string().required('نوع الزامی است'),
  capacity: Yup.string().nullable(true),
  event_important: Yup.string().required('انتخاب رویدااد مهم الزامی است'),
  description: Yup.string().required('توضیحات الزامی است'),
  team_form_id: Yup.string().nullable(true),
  min_user: Yup.number().nullable(true),
  max_user: Yup.number().nullable(true),
  free: Yup.number()
    .typeError('رایگان/غیر رایگان الزامی است')
    .required('رایگان/غیر رایگان الزامی است'),
  price_type: Yup.number().typeError('نوع پرداخت اشتباه است'),
  price: Yup.number()
    .typeError('هزینه ثبت نام باید عدد باشد')
    .min(1000, 'کمترین مقدار هزینه ثبت نام باید 1000 تومان باشد'),
  step_eventing: Yup.number()
    .typeError('فیلد رویداد مرحله ای الزامی است')
    .required('فیلد رویداد مرحله ای الزامی است'),
  fields: Yup.array().typeError(''),
  fandom_type: Yup.number()
    .typeError('فیلد هواداری الزام یاست')
    .required('فیلد هواداری الزام یاست'),
  link: Yup.string().nullable(),
});

const Create = () => {
  const navigate = useNavigate();
  const { canBoard, can } = useRole();
  const eventContext = useContext(EventContext);
  const [loading, setLoading] = useState(true);
  const [photoIds, setPhotoIds] = useState([]);
  const [rolesId, setRolesId] = useState([]);
  const [submitFormLoading, setSubmitFormLoading] = useState(false);
  const [enableFormTeam, setEnableFormTeam] = useState(false);

  useEffect(() => {
    getProvinces();
    getOrganizers();
    getCategories();
    getFields();
    getForms();
  }, []);

  const getProvinces = async () => {
    await useApi.get(
      '/province',
      {},
      (res) => {
        let provinces = [...res.data.data];
        let last = provinces?.pop();
        provinces?.unshift(last);
        if (canBoard() && !can('Dyaco Admin') && !can('Super Admin')) {
          eventContext.provinces = provinces.filter(
            (p) => p.id === eventContext.authUser.province_id,
          );
        } else {
          eventContext.provinces = provinces;
        }
      },
      (err) => {
        setLoading(false);
        error('خطا در دریافت اطلاعات');
      },
    );
  };

  const getOrganizers = async () => {
    await useApi.get(
      '/organizer?no-paginate=true',
      {},
      (res) => {
        eventContext.organizers = res.data.data;
      },
      (err) => {
        setLoading(false);
        error('خطا در دریافت اطلاعات');
      },
    );
  };

  const getCategories = async () => {
    await useApi.get(
      '/category?no-paginate=true',
      {},
      (res) => {
        let data = res.data.data;
        if (can('Dyaco Admin') || can('Super Admin')) {
          eventContext.categories = data;
        } else {
          eventContext.categories = data.filter((c) => c.title == 'استانی');
        }
      },
      (err) => {
        setLoading(false);
        error('خطا در دریافت اطلاعات');
      },
    );
  };

  const getFields = async () => {
    await useApi.get(
      '/sport-field?no-paginate=true',
      {},
      (res) => {
        let data = res.data.data;
        eventContext.fields = data;
      },
      (err) => {
        setLoading(false);
        error('هنگام دریافت اطلاعات خطایی رخ داده است');
      },
    );
  };

  const getForms = async () => {
    await useApi.get(
      '/form?no-paginate=true',
      {},
      (res) => {
        let data = res.data.data;
        eventContext.forms = [];
        eventContext.forms = data;
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        error('هنگام دریافت اطلاعات خطایی رخ داده است');
      },
    );
  };

  const onSubmitForm = async (values, setSubmitting) => {
    if (photoIds.length > 0) {
      let photoId = photoIds[0].serverId;
      let thumbnail = JSON.parse(photoId);
      values['thumbnail'] = thumbnail.data.path;
    }

    if (rolesId.length > 0) {
      let roles = _.map(rolesId, 'serverId');
      let roles_id = [];
      roles.map((item) => {
        let { data } = JSON.parse(item);
        roles_id.push(data.path);
      });
      values['roles'] = roles_id;
    }

    if (values.fields.length > 0) {
      values['fields'] = values.fields?.map((f) => f.id);
    }

    setSubmitFormLoading(true);
    const payload = {
      ...values,
      start_register_date: jalaliDateToGregorianApi(values.start_register_date),
      end_register_date: jalaliDateToGregorianApi(
        values.end_register_date,
      )?.replace('00:00:00', '23:59:59'),
    };

    await useApi.post(
      '/event',
      payload,
      (res) => {
        success('رویداد با موفقیت ایجاد شد');
        navigate('/panel/event', { replace: true });
      },
      (err) => {
        setSubmitFormLoading(false);
        error('خطایی در ثبت اطلاعات پیش آمده است.');
      },
    );
  };

  const textEditorImageUpload = async (files, info, uploadHandler) => {
    let data = {
      photo: files[0],
    };
    await useApi.post(
      '/event/image-upload',
      data,
      (res) => {
        uploadHandler({
          result: [
            {
              url: res.data.data.url ?? res.data.data.path,
              name: files[0].name,
              size: files[0].size,
            },
          ],
        });
      },
      (err) => {
        uploadHandler('error');
        error('هنگام آپلود تصویر خطایی پییش آمده است');
      },
      true,
    );
  };

  return loading ? (
    <Loading />
  ) : (
    <Master>
      <Formik
        initialValues={{
          title: '',
          start_register_date: '',
          end_register_date: '',
          time: '',
          place: '',
          gender: '',
          province_id: '',
          organizer_id: '',
          type: '',
          capacity: '',
          description: '',
          form_id: '',
          category_id: '',
          event_important: '',
          team_form_id: '',
          free: '',
          price: '',
          step_eventing: '',
          price_type: '',
          min_user: '',
          max_user: '',
          province_access: '',
          fields: [],
          fandom_type: 0,
          link: '',
          registered_by: 'All',
        }}
        onSubmit={(values, { setSubmitting }) => {
          onSubmitForm(values, setSubmitting);
        }}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <div className='row border rounded p-2 mt-3'>
              <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>عنوان رویداد</label>
                    <input
                      name='title'
                      value={values.title}
                      onChange={handleChange}
                      type='text'
                      className='form-control'
                    />
                    <ErrorMessage error={errors.title} />
                  </div>
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>
                      لینک رویداد (درصورتی که نیاز به تغیر لینک رویداد دارید
                      وارد شود‌)
                    </label>
                    <input
                      name='link'
                      value={values.link}
                      onChange={handleChange}
                      type='text'
                      className='form-control'
                    />
                    <ErrorMessage error={errors.link} />
                  </div>
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column'>
                    <label className='f-14 mx-2'>فرم</label>
                    <select
                      value={values.form_id}
                      name='form_id'
                      onChange={handleChange}
                      className='form-control'
                    >
                      <option value='انتخاب'>انتخاب</option>
                      {eventContext.forms.length > 0 &&
                        eventContext.forms?.map((item) => (
                          <option value={item.id} key={item.id}>
                            {item.title}
                          </option>
                        ))}
                    </select>
                    <ErrorMessage error={errors.form_id} />
                  </div>
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>دسته بندی</label>
                    <select
                      className='form-control'
                      name='category_id'
                      value={values.category_id}
                      onChange={handleChange}
                    >
                      <option>انتخاب کنید</option>

                      {eventContext.categories.length > 0 &&
                        eventContext.categories?.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.title}
                          </option>
                        ))}
                    </select>
                    <ErrorMessage error={errors.category_id} />
                  </div>
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column mt-1 mx-2'>
                    <label>ثبت نام کننده</label>
                    <select
                      className='form-control'
                      name='registered_by'
                      value={values.registered_by}
                      onChange={handleChange}
                    >
                      <option>انتخاب کنید</option>

                      {getRegistredBy?.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                    <ErrorMessage error={errors.registered_by} />
                  </div>
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                <div className='form-group'>
                  <div className='d-flex flex-column'>
                    <label className='f-14 mx-2'>رویداد مهم</label>
                    <select
                      value={values.event_important}
                      name='event_important'
                      onChange={handleChange}
                      className='form-control'
                    >
                      <option value='0'>نمی باشد</option>
                      <option value='1'>می باشد</option>
                    </select>
                    <ErrorMessage error={errors.event_important} />
                  </div>
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <div className='d-flex flex-column mx-1'>
                    <label>تاریخ شروع ثبت نام</label>
                    <DatePicker
                      name='start_register_date'
                      value={values.start_register_date}
                      handleChange={setFieldValue}
                    />
                    <ErrorMessage error={errors.start_register_date} />
                  </div>
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <div className='d-flex flex-column mx-1'>
                    <label>تاریخ پایان ثبت نام</label>
                    <DatePicker
                      name='end_register_date'
                      handleChange={setFieldValue}
                      value={values.end_register_date}
                    />
                    <ErrorMessage error={errors.end_register_date} />
                  </div>
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <div className='d-flex flex-column mx-1'>
                    <label>زمان</label>
                    <input
                      name='time'
                      value={values.time}
                      onChange={handleChange}
                      type='text'
                      className='form-control'
                    />
                    <ErrorMessage error={errors.time} />
                  </div>
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <div className='d-flex flex-column mx-1'>
                    <label>مکان</label>
                    <input
                      type='text'
                      name='place'
                      value={values.place}
                      onChange={handleChange}
                      className='form-control'
                    />
                    <ErrorMessage error={errors.place} />
                  </div>
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <label>جنسیت</label>
                  <select
                    className='form-control'
                    name='gender'
                    value={values.gender}
                    onChange={handleChange}
                  >
                    <option>انتخاب کنید</option>
                    <option value='1'>مرد</option>
                    <option value='2'>زن</option>
                    <option value='3'>هردو</option>
                  </select>
                  <ErrorMessage error={errors.gender} />
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <label>استان محل برگذاری</label>
                  <select
                    className='form-control'
                    name='province_id'
                    value={values.province_id}
                    onChange={handleChange}
                  >
                    <option>انتخاب کنید</option>

                    {eventContext.provinces.length > 0 &&
                      eventContext.provinces?.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                  </select>
                  <ErrorMessage error={errors.province_id} />
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <label>نوع</label>
                  <select
                    className='form-control'
                    name='type'
                    value={values.type}
                    onChange={(e) => {
                      if (e.target.value == 2 || e.target.value == 3) {
                        setEnableFormTeam(true);
                      } else {
                        setEnableFormTeam(false);
                      }
                      setFieldValue('type', e.target.value, true);
                    }}
                  >
                    <option>انتخاب کنید</option>
                    <option value='1'>انفرادی</option>
                    <option value='2'>تیمی</option>
                    <option value='3'>رویدادی</option>
                  </select>
                  <ErrorMessage error={errors.type} />
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <label>هواداری</label>
                  <select
                    className='form-control'
                    name='fandom_type'
                    value={values.fandom_type}
                    onChange={handleChange}
                  >
                    <option>انتخاب کنید</option>
                    <option value='1'>بله</option>
                    <option value='0'>خیر</option>
                  </select>
                  <ErrorMessage error={errors.fandom_type} />
                </div>
              </div>
              {enableFormTeam && (
                <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                  <div className='form-group'>
                    <label>فرم تیم</label>
                    <select
                      className='form-control'
                      name='team_form_id'
                      value={values.team_id}
                      onChange={handleChange}
                    >
                      <option>انتخاب کنید</option>
                      {eventContext.forms.length > 0 &&
                        eventContext.forms?.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.title}
                          </option>
                        ))}
                    </select>
                    <ErrorMessage error={errors.team_form_id} />
                  </div>
                </div>
              )}

              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <label>ظرفیت</label>
                  <input
                    name='capacity'
                    value={values.capacity}
                    onChange={handleChange}
                    type='number'
                    className='form-control'
                  />
                  <ErrorMessage error={errors.capacity} />
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <label>حداقل کاربر ثبت نامی (مخصوص مسابقات تیمی )</label>
                  <input
                    name='min_user'
                    value={values.min_user}
                    onChange={handleChange}
                    type='number'
                    className='form-control'
                  />
                  <ErrorMessage error={errors.min_user} />
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <label>حداکثر کاربر ثبت نامی (مخصوص مسابقات تیمی )</label>
                  <input
                    name='max_user'
                    value={values.max_user}
                    onChange={handleChange}
                    type='number'
                    className='form-control'
                  />
                  <ErrorMessage error={errors.max_user} />
                </div>
              </div>

              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <label className='f-14 mx-2'>برگزار کننده</label>
                  <select
                    value={values.organizer_id}
                    name='organizer_id'
                    onChange={handleChange}
                    className='form-control'
                  >
                    <option value='انتخاب'>انتخاب</option>
                    {eventContext.organizers.length > 0 &&
                      eventContext.organizers?.map((item) => (
                        <option value={item.id} key={item.id}>
                          {item.name}
                        </option>
                      ))}
                  </select>
                  <ErrorMessage error={errors.organizer_id} />
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <label className='f-14 mx-2'>دسترسی استان ها</label>
                  <select
                    value={values.province_access}
                    name='province_access'
                    onChange={handleChange}
                    className='form-control'
                  >
                    <option value='انتخاب'>انتخاب</option>
                    <option value='1'>اجازه دسترسی</option>
                    <option value='0'>عدم دسترسی</option>
                  </select>
                  <ErrorMessage error={errors.province_access} />
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <label className='f-14 mx-2'>رویداد مرحله ای</label>
                  <select
                    value={values.step_eventing}
                    name='step_eventing'
                    onChange={handleChange}
                    className='form-control'
                  >
                    <option value='انتخاب'>انتخاب</option>

                    <option value='0'>عادی</option>
                    <option value='1'>مرحله ای</option>
                  </select>
                  <ErrorMessage error={errors.step_eventing} />
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <label className='f-14 mx-2'>رایگان/غیر رایگان</label>
                  <select
                    value={values.free}
                    name='free'
                    onChange={handleChange}
                    className='form-control'
                  >
                    <option value='انتخاب'>انتخاب</option>

                    <option value='1'>رایگان</option>
                    <option value='0'>غیر رایگان</option>
                  </select>
                  <ErrorMessage error={errors.free} />
                </div>
              </div>
              {values?.free == 0 && (
                <>
                  <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                    <div className='form-group'>
                      <label className='f-14 mx-2'>نوع پرداخت</label>
                      <select
                        value={values.price_type}
                        name='price_type'
                        onChange={handleChange}
                        className='form-control'
                      >
                        <option value='انتخاب'>انتخاب</option>
                        <option value='1'>مبلغ ثابت</option>
                        <option value='2'>مبلغ به ازای هر عضو تیم</option>
                      </select>
                      <ErrorMessage error={errors.price_type} />
                    </div>
                  </div>

                  <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                    <div className='form-group'>
                      <label> هزینه ثبت نام (تومان)</label>
                      <input
                        name='price'
                        value={values.price}
                        onChange={handleChange}
                        type='number'
                        className='form-control'
                      />
                      <ErrorMessage error={errors.price} />
                    </div>
                  </div>
                </>
              )}
              <div
                className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'
                style={{ zIndex: '99999999999999999999999999' }}
              >
                <div className='form-group'>
                  <label className='text-dark f-14 text-left'>
                    رشته ها(فقط زمانی انتخاب شود که در فرم مربوط به رویداد فیلد
                    رشته وجود دارد.)
                  </label>
                  <Select
                    isMulti
                    closeMenuOnSelect={false}
                    options={eventContext.fields}
                    onChange={(value) => setFieldValue('fields', value, true)}
                    noOptionsMessage={() => 'لطفا رشته ها را انتخاب کنید'}
                    getOptionLabel={(c) => c.name}
                    getOptionValue={(c) => c.id}
                    styles={{ zIndex: '99999999999999999' }}
                    className='w-100 text-dark'
                    placeholder='انتخاب کنید...'
                  />
                  <ErrorMessage classes='my-2' error={errors.fields} />
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <label>تصویر رویداد</label>
                  <FileUploader
                    files={photoIds}
                    roles={['image/jpeg', 'image/jpg', 'image/png']}
                    setFiles={setPhotoIds}
                    allowMultiple={false}
                    maxFiles={1}
                    maxFileSize='1024KB'
                    serverUrl={`${baseApiUrl()}/event/thumbnail-upload`}
                    method='POST'
                    headers={{
                      Accept: 'application/json',
                      Authorization: getStoredAuthToken(),
                    }}
                  />
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-3 col-lg-3 col-xl-3'>
                <div className='form-group'>
                  <label>فایل های قوانین</label>
                  <FileUploader
                    files={rolesId}
                    roles={['application/pdf']}
                    setFiles={setRolesId}
                    allowMultiple={true}
                    maxFiles={10}
                    name='file'
                    maxFileSize='2048KB'
                    serverUrl={`${baseApiUrl()}/event/roles-upload`}
                    method='POST'
                    headers={{
                      Accept: 'application/json',
                      Authorization: getStoredAuthToken(),
                    }}
                  />
                </div>
              </div>
              <div className='col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12'>
                <div className='form-group'>
                  <label>توضیحات</label>

                  {/*<TextEditor
									heightEditor='500px'
									tagName='textAria'
									textareaName='fullDescription'
									filedValue={values.fullDescription}
									initialValue=''
									setFieldValue={setFieldValue}
									imageUpload={imageUploadTextEditor}
									inputFileId='textEditorFile'
									inputFileName='photo'
										/>*/}
                  <SunTextEditor
                    handelChange={setFieldValue}
                    textEditorImageUpload={textEditorImageUpload}
                  />
                </div>
                <ErrorMessage error={errors.description} />
              </div>
            </div>
            <div className='col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 my-3'>
              <div className='d-flex flex-row justify-content-end'>
                <button className='btn btn-info btn-sm f-14'>
                  {submitFormLoading ? (
                    <div
                      className='spinner-border spinner-border-sm text-white mb-1'
                      role='status'
                    ></div>
                  ) : (
                    'ثبت رویداد'
                  )}
                </button>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </Master>
  );
};

export default Create;
